'use client'

import { useSmoothScroll } from '@/hooks/use-smooth-scroll'
import { Link } from '@/i18n/navigation'
import { TButtonVariant } from '@/lib/types/button.types'
import { cn } from '@/lib/utils'
import React, { MouseEvent } from 'react'

interface IClientSmoothScrollButtonProps {
  id?: string
  label?: string
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
  variant?: TButtonVariant
  className?: string
  disabled?: boolean
  href: string
  scrollOffset?: number
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void
}

export function ClientSmoothScrollButton({
  id,
  label,
  icon,
  iconPosition = 'left',
  variant = 'primary',
  className = '',
  disabled = false,
  href,
  scrollOffset = 120,
  onClick,
  ...props
}: IClientSmoothScrollButtonProps) {
  const { scrollToAnchor } = useSmoothScroll({ offset: scrollOffset })

  const isIconOnly = !label && Boolean(icon)
  const baseClasses = cn('base-btn', isIconOnly ? 'p-2' : 'py-5 px-8')

  const buttonVariants = {
    primary:
      'bg-secondary-500 hover:bg-secondary-600 active:bg-secondary-700 disabled:bg-primary-200 text-primary-500',
    secondary:
      'bg-primary-500 hover:bg-primary-700 active:bg-primary-800 text-white',
    tertiary: 'bg-gray-500 hover:bg-tertiary-800 text-primary',
    ghost: 'bg-none border-0 hover:bg-gray-100 text-primary'
  }

  const hashIndex = href.indexOf('#')
  const isCrossPageNavigation = hashIndex > 0

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    scrollToAnchor(href)
    onClick?.(e)
  }

  const content = (
    <>
      {!isIconOnly && icon && iconPosition === 'left' && (
        <span className="mr-2 inline-flex items-center">{icon}</span>
      )}
      {isIconOnly ? icon : label}
      {!isIconOnly && icon && iconPosition === 'right' && (
        <span className="ml-2 inline-flex items-center">{icon}</span>
      )}
    </>
  )

  // For cross-page navigation with hash, use Link with scroll=false
  // This prevents default scroll and lets ScrollToHash component handle it with offset
  if (isCrossPageNavigation) {
    return (
      <Link href={href} scroll={false}>
        <button
          id={id}
          disabled={disabled}
          className={cn(baseClasses, buttonVariants[variant], className)}
          aria-label={label}
          {...props}
        >
          {content}
        </button>
      </Link>
    )
  }

  // For same-page hash navigation, use button with click handler
  return (
    <button
      id={id}
      onClick={handleClick}
      disabled={disabled}
      className={cn(baseClasses, buttonVariants[variant], className)}
      aria-label={label}
      {...props}
    >
      {content}
    </button>
  )
}
